import { GlideIcon } from "@glide/common";
import { motion } from "framer-motion";
import { VoiceVisualizer, type VoiceVisualizerHandle } from "./voice-visualizer";
import { useEffect, useRef, useState } from "react";
import { isNonNullDefined } from "@glide/support";
import type { CoreTheme } from "@glideapps/theme";
import "twin.macro";

interface RecorderUIProps {
    readonly onStartRecording: () => void;
    readonly onCancelRecording: () => void;
    readonly onFinishRecording: () => void;
    readonly state: "idle" | "recording" | "processing" | "error";
    readonly startRecordingOnMount: boolean;
    readonly getAudioData: () => Float32Array;
    readonly getFormattedTime: () => string;
    readonly theme: CoreTheme;
    readonly variant: "voice-entry" | "chat-popover";
    readonly disabled: boolean;
}

export const RecorderUI: React.FC<RecorderUIProps> = p => {
    const {
        theme,
        onStartRecording,
        onCancelRecording,
        onFinishRecording,
        state,
        getAudioData,
        getFormattedTime,
        startRecordingOnMount,
        variant,
        disabled,
    } = p;

    const visualizerRef = useRef<VoiceVisualizerHandle>(null);
    const isInitialRender = useRef(true);
    const [displayTime, setDisplayTime] = useState("00:00");

    useEffect(() => {
        if (isInitialRender.current && startRecordingOnMount && !disabled) {
            onStartRecording();
            isInitialRender.current = false;
        }
    }, [onStartRecording, startRecordingOnMount, disabled]);

    useEffect(() => {
        let intervalId: NodeJS.Timeout | undefined;

        const updateDisplayTime = () => {
            setDisplayTime(getFormattedTime());
        };

        if (state === "recording") {
            updateDisplayTime();
            intervalId = setInterval(updateDisplayTime, 1000);
        }

        return () => {
            if (isNonNullDefined(intervalId)) {
                clearInterval(intervalId);
            }
        };
    }, [state, getFormattedTime]);

    const isIdle = state === "idle";

    const handleCancelRecording = () => {
        visualizerRef.current?.cleanup();
        onCancelRecording();
    };

    const handleFinishRecording = () => {
        visualizerRef.current?.cleanup();
        onFinishRecording();
    };

    const iconSize = 20;

    return (
        <div tw="relative w-full h-11">
            {/* Inactive state layer */}
            <motion.div
                initial={{ opacity: 1 }}
                animate={{ opacity: !isIdle ? 0 : 1 }}
                transition={{
                    duration: 0.2,
                }}
                style={{
                    pointerEvents: !isIdle ? "none" : "auto",
                }}
                tw="absolute inset-0 z-10 flex items-center justify-end rounded-full px-2 pr-1.5 w-fit justify-self-end ml-auto">
                {variant === "chat-popover" ? (
                    <motion.button
                        type="button"
                        whileTap={{ scale: 0.95 }}
                        transition={{
                            type: "spring",
                            stiffness: 400,
                            damping: 17,
                        }}
                        disabled={disabled}
                        tw="flex items-center justify-center !w-7 !h-7 rounded-[36px] bg-transparent"
                        onClick={onStartRecording}>
                        <GlideIcon
                            icon={"st-microphone"}
                            kind="stroke"
                            iconSize={iconSize}
                            tw="text-icon-base hover:text-icon-dark"
                        />
                    </motion.button>
                ) : (
                    <motion.button
                        type="button"
                        whileTap={{ scale: 0.95 }}
                        transition={{
                            type: "spring",
                            stiffness: 400,
                            damping: 17,
                        }}
                        disabled={disabled}
                        tw="flex items-center justify-center !w-9 !h-9 rounded-[36px] !bg-accent"
                        onClick={onStartRecording}>
                        <GlideIcon icon={"st-microphone"} kind="stroke" iconSize={iconSize} tw="text-w100A" />
                    </motion.button>
                )}
            </motion.div>

            {/* Active state layer */}
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: !isIdle ? 1 : 0 }}
                data-error={state === "error"}
                data-variant={variant}
                transition={{
                    duration: 0.2,
                }}
                style={{
                    pointerEvents: !isIdle ? "auto" : "none",
                    visibility: !isIdle ? "visible" : "hidden",
                }}
                tw="absolute inset-0 z-20 flex items-center
                    data-[variant=chat-popover]:bg-transparent border border-transparent
                bg-n200 data-[error=true]:bg-r200 rounded-full">
                <div tw="flex items-center gap-2 w-full h-full px-2 pr-1.5">
                    <motion.button
                        whileTap={{ scale: 0.95 }}
                        data-error={state === "error"}
                        disabled={state === "processing"}
                        transition={{
                            type: "spring",
                            stiffness: 400,
                            damping: 17,
                        }}
                        tw="flex items-center justify-center shrink-0 !w-6 !h-6 rounded-[36px] bg-n200A data-[error=true]:bg-r300 text-icon-dark data-[error=true]:text-r500"
                        onClick={handleCancelRecording}>
                        <GlideIcon icon={"st-close"} kind="stroke" iconSize={16} />
                    </motion.button>

                    <div tw="flex-1 h-full">
                        <VoiceVisualizer
                            ref={visualizerRef}
                            getAudioData={getAudioData}
                            isRecording={state === "recording"}
                            barColor={state === "error" ? theme.r500 : theme.iconDark}
                            secondaryBarColor={state === "error" ? theme.r300 : theme.iconPale}
                        />
                    </div>

                    <motion.p
                        initial={{ opacity: 0 }}
                        animate={{ opacity: !isIdle ? 1 : 0 }}
                        data-error={state === "error"}
                        transition={{
                            duration: 0.2,
                        }}
                        tw="tabular-nums shrink-0 font-semibold text-builder-sm text-text-base data-[error=true]:text-r500">
                        {state === "error" ? "Error" : state === "processing" ? "00:00" : displayTime}
                    </motion.p>

                    <motion.button
                        type="button"
                        whileTap={{ scale: 0.95 }}
                        data-error={state === "error"}
                        disabled={state === "processing"}
                        data-variant={variant}
                        transition={{
                            type: "spring",
                            stiffness: 400,
                            damping: 17,
                        }}
                        tw="flex items-center justify-center !w-9 !h-9 rounded-[36px] !bg-accent data-[error=true]:!bg-r400 data-[variant=chat-popover]:(!w-7 !h-7)"
                        onClick={handleFinishRecording}>
                        {state === "processing" ? (
                            <GlideIcon icon={"st-spinner"} kind="stroke" iconSize={iconSize} tw="text-w100A" spin />
                        ) : state === "error" ? (
                            <GlideIcon icon={"st-reload"} kind="stroke" iconSize={iconSize} tw="text-w100A" />
                        ) : (
                            <GlideIcon icon={"st-check"} kind="stroke" iconSize={iconSize} tw="text-w100A" />
                        )}
                    </motion.button>
                </div>
            </motion.div>
        </div>
    );
};
